import { useActionSheet } from '@expo/react-native-action-sheet';
import { AnalyticsUserEvent } from '@fruitful/types';
import Clipboard from '@react-native-clipboard/clipboard';
import { useFeatureGate } from '@statsig/expo-bindings';
import * as Application from 'expo-application';
import { router } from 'expo-router';
import * as Updates from 'expo-updates';
import { useUpdates } from 'expo-updates';
import { useSetAtom } from 'jotai';
import { type ReactNode, useState } from 'react';
import {
    ArrowRightIcon,
    BadgeLabel,
    BellIcon,
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    ChartPieIcon,
    ClipboardCheckIcon,
    CopyIcon,
    FileChartPieIcon,
    FruitfulCustomIcon,
    FruitfulLogo,
    FullScreenSpinner,
    Icon,
    IdCardIcon,
    Image,
    isWeb,
    LandmarkIcon,
    LogOutIcon,
    MailboxIcon,
    MessageCircleQuestionMarkIcon,
    NavigationRow,
    Row,
    RowList,
    ScanFaceIcon,
    ScreenContainer,
    Spinner,
    SproutIcon,
    UserRoundIcon,
    UsersRoundIcon,
    useThemeColors,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import { useLogout } from '../../auth/hooks/useLogout';
import { useUserAuthorization } from '../../auth/hooks/useUserAuthorization';
import { LogoutType } from '../../auth/types/Auth';
import { billReviewSourceAtom } from '../../bills/state/BillsAtoms';
import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { IS_DEV } from '../../config/FruitfulEnvironment';
import { FeatureFlags } from '../../feature-flags/FeatureFlags';
import LogRocketFacade from '../../logger/log-rocket/LogRocketFacade';
import { track } from '../../logger/Logger';
import { usePreMoneyMapCompleteness } from '../../onboarding/hooks/usePreMoneyMapCompleteness';
import { ReferralsCtaCard } from '../../referrals/components/ReferralsCtaCard';

const CompletenessBadge = ({ text }: { text: string }) => {
    return (
        <BadgeLabel
            size="large"
            text={text}
            icon={ClipboardCheckIcon}
            variant="warning"
        />
    );
};

export const Settings = () => {
    LogRocketFacade.tagPage('Settings');

    const setBillReviewSource = useSetAtom(billReviewSourceAtom);
    const [showSpinner, setShowSpinner] = useState(false);

    // Set context for if/when Bills is navigated to from the Settings page.
    setBillReviewSource('profile');

    const { value: enableBillingSettings } = useFeatureGate(
        FeatureFlags.ENABLE_BILLING_SETTINGS,
    );

    if (showSpinner) {
        return <FullScreenSpinner />;
    }

    return (
        <ScreenContainer
            title="Account"
            hasCloseButton={true}
            leftAction={
                enableBillingSettings ? <EarnReferralPill /> : undefined
            }
            sideMinWidthOverride={enableBillingSettings ? 100 : undefined}
        >
            <YStack gap={24}>
                {!enableBillingSettings && (
                    <ReferralsCtaCard showPreMoneyMap={true} />
                )}
                {enableBillingSettings ? (
                    <MembershipAndGuidanceSection />
                ) : (
                    <GeneralSettingsSection />
                )}
                <ProfileSettingsSection />
                <FinancesSettingsSection />
                <PreferencesSettingsSection />
                <SignOutSettingsSection
                    onSignOutPress={() => {
                        setShowSpinner(true);
                    }}
                />
                {!isWeb && <AboutSection />}
            </YStack>
        </ScreenContainer>
    );
};

const EarnReferralPill = () => {
    return (
        <XStack
            backgroundColor="$leaf"
            borderRadius={16}
            paddingHorizontal={12}
            paddingVertical={8}
            cursor="pointer"
            onPress={() => {
                track(AnalyticsUserEvent.REFERRALS_CTA_PRESSED);
                router.navigate('/referrals');
            }}
        >
            <BodyMediumEmphasized color="$white">
                Want $100?
            </BodyMediumEmphasized>
        </XStack>
    );
};

const ShortcutTile = ({
    icon,
    title,
    subtitle,
    onPress,
}: {
    icon: ReactNode;
    title: string;
    subtitle: string;
    onPress: () => void;
}) => {
    return (
        <YStack
            flex={1}
            flexBasis={0}
            minWidth={0}
            alignItems="flex-start"
            backgroundColor="$gray100"
            borderRadius={24}
            padding={16}
            gap={12}
            cursor="pointer"
            onPress={onPress}
        >
            {icon}
            <YStack gap={4}>
                <BodyMediumRegular>{title}</BodyMediumRegular>
                <BodySmallRegular color="$gray1000">
                    {subtitle}
                </BodySmallRegular>
            </YStack>
        </YStack>
    );
};

const MembershipAndGuidanceSection = () => {
    const user = useAuthedUser();
    const guideImageUrl = user.membership?.guide?.profileImageUrl;
    const guideIcon =
        guideImageUrl != null && guideImageUrl !== '' ? (
            <Image
                source={{ uri: guideImageUrl }}
                width={40}
                height={40}
                borderRadius={20}
            />
        ) : (
            <YStack
                width={40}
                height={40}
                alignItems="center"
                justifyContent="center"
                borderRadius={20}
                backgroundColor="$peachLight"
            >
                <Icon icon={UserRoundIcon} size={20} color="$peachDarkest" />
            </YStack>
        );

    return (
        <YStack gap={16}>
            <XStack gap={16} width="100%">
                <ShortcutTile
                    icon={
                        <FruitfulCustomIcon
                            icon="fruitful"
                            color="peach"
                            size="sm"
                        />
                    }
                    title="Membership"
                    subtitle="Manage your plan and billing"
                    onPress={() => {
                        router.navigate('/settings/membership');
                    }}
                />
                <ShortcutTile
                    icon={guideIcon}
                    title="Guidance"
                    subtitle="Book sessions with your Guide"
                    onPress={() => {
                        router.navigate('/home/my-guide');
                    }}
                />
            </XStack>
            <RowList>
                <NavigationRow
                    title="Get support"
                    subtitle="Contact us • FAQs"
                    leftIcon={MessageCircleQuestionMarkIcon}
                    onPress={() => {
                        router.navigate('/settings/support');
                    }}
                />
            </RowList>
        </YStack>
    );
};

const GeneralSettingsSection = () => {
    const { getColor } = useThemeColors();

    return (
        <RowList>
            <NavigationRow
                title="Membership"
                subtitle="Plan details"
                leftContentOverride={
                    <XStack
                        backgroundColor="$green300"
                        padding={4}
                        borderRadius={8}
                    >
                        <FruitfulLogo
                            width={12}
                            height={12}
                            color={getColor('$black')}
                        />
                    </XStack>
                }
                onPress={() => {
                    router.navigate('/settings/subscription');
                }}
            />
            <NavigationRow
                title="Get support"
                subtitle="Contact us • FAQs"
                leftIcon={MessageCircleQuestionMarkIcon}
                onPress={() => {
                    router.navigate('/settings/support');
                }}
            />
        </RowList>
    );
};

const ProfileSettingsSection = () => {
    return (
        <RowList rowHeaderProps={{ title: 'Profile' }}>
            <NavigationRow
                title="Personal info"
                subtitle="Name • Pronouns • Date of birth"
                leftIcon={IdCardIcon}
                onPress={() => {
                    router.navigate('/profile/personal-info');
                }}
            />
            <NavigationRow
                title="Address"
                subtitle="Home address"
                leftIcon={MailboxIcon}
                onPress={() => {
                    router.navigate('/profile/address');
                }}
            />
        </RowList>
    );
};

const FinancesSettingsSection = () => {
    const {
        isFinancialProfileComplete,
        hasViewedLinkMoreAccounts,
        isLoading: isLoadingPreMoneyMapCompleteness,
    } = usePreMoneyMapCompleteness();
    const user = useAuthedUser();

    return (
        <>
            {isLoadingPreMoneyMapCompleteness ? (
                <Spinner />
            ) : (
                <RowList rowHeaderProps={{ title: 'Finances' }}>
                    <Row
                        title="Financial profile"
                        subtitle="Income • Goals • Insurance"
                        leftIcon={SproutIcon}
                        onPress={() => {
                            router.navigate('/profile/financial-profile');
                        }}
                        rightContent={
                            isFinancialProfileComplete ? (
                                <Icon color="$black" icon={ArrowRightIcon} />
                            ) : (
                                <CompletenessBadge text="Finish" />
                            )
                        }
                    />
                    <Row
                        title="External accounts"
                        subtitle="Spend • Debt • Investment"
                        leftIcon={LandmarkIcon}
                        onPress={() => {
                            router.navigate('/profile/external-accounts');
                        }}
                        rightContent={
                            hasViewedLinkMoreAccounts ? (
                                <Icon color="$black" icon={ArrowRightIcon} />
                            ) : (
                                <CompletenessBadge text="Link" />
                            )
                        }
                    />
                    <NavigationRow
                        title="Bills"
                        subtitle="Household bills"
                        leftIcon={FileChartPieIcon}
                        onPress={() => {
                            router.navigate('/expenses/bills-review');
                        }}
                    />
                    <NavigationRow
                        title="Spend"
                        subtitle="Average monthly household spend"
                        leftIcon={ChartPieIcon}
                        onPress={() => {
                            router.navigate('/profile/spend');
                        }}
                    />
                    {user.partnerMember == null && (
                        <NavigationRow
                            title="Add a partner"
                            subtitle="Add your partner"
                            leftIcon={UsersRoundIcon}
                            onPress={() => {
                                router.navigate('/settings/add-partner');
                            }}
                        />
                    )}
                </RowList>
            )}
        </>
    );
};

const PreferencesSettingsSection = () => {
    const { accessToRestrictedCapabilities } = useUserAuthorization();

    const canAccessNotifications =
        accessToRestrictedCapabilities.guideMessaging === true ||
        accessToRestrictedCapabilities.guidanceSessions === true;

    return (
        <RowList rowHeaderProps={{ title: 'Preferences' }}>
            {canAccessNotifications && (
                <NavigationRow
                    title="Notifications"
                    subtitle={isWeb ? 'Email' : 'Email • Push notifications'}
                    leftIcon={BellIcon}
                    onPress={() => {
                        router.navigate('/settings/notifications');
                    }}
                />
            )}
            {!isWeb && (
                <NavigationRow
                    title="Security"
                    subtitle="Biometrics • MFA"
                    leftIcon={ScanFaceIcon}
                    onPress={() => {
                        router.navigate('/settings/security');
                    }}
                />
            )}
        </RowList>
    );
};

interface SignOutSettingsSectionProps {
    onSignOutPress: () => void;
}

const SignOutSettingsSection = ({
    onSignOutPress,
}: SignOutSettingsSectionProps) => {
    const { showActionSheetWithOptions } = useActionSheet();
    const { logOut } = useLogout();

    const handleSignOut = () => {
        onSignOutPress();
        void logOut(LogoutType.LogoutButton);
    };

    const showSignOutOptionSheet = () => {
        const options = ['Sign out', 'Cancel'];
        const destructiveButtonIndex = 0;
        const cancelButtonIndex = 1;

        showActionSheetWithOptions(
            {
                options,
                destructiveButtonIndex,
                tintColor: '#007AFF',
            },
            (selectedIndex?: number) => {
                switch (selectedIndex) {
                    case destructiveButtonIndex:
                        handleSignOut();
                        break;
                    case cancelButtonIndex:
                    default:
                        break;
                }
            },
        );
    };

    const onPress = () => {
        isWeb ? handleSignOut() : showSignOutOptionSheet();
    };

    return (
        <RowList>
            <Row title="Sign out" leftIcon={LogOutIcon} onPress={onPress} />
        </RowList>
    );
};

const AboutSection = () => {
    const toast = useToastController();
    const { isUpdateAvailable } = useUpdates();

    const version = `${Application?.nativeApplicationVersion ?? ''} (${Application.nativeBuildVersion})`;
    const updateId = IS_DEV
        ? 'Dev'
        : Updates.updateId != null
          ? `${Updates.updateId}${isUpdateAvailable ? '*' : ''}`
          : 'N/A';

    return (
        <YStack gap={16}>
            <YStack>
                <BodyMediumRegular>Version:</BodyMediumRegular>
                <XStack
                    gap={4}
                    alignItems="center"
                    cursor="pointer"
                    onPress={() => {
                        Clipboard.setString(version);
                        toast.show('Copied!', { duration: 1000 });
                    }}
                >
                    <BodyMediumRegular>{version}</BodyMediumRegular>
                    <Icon icon={CopyIcon} size={12} color="$black" />
                </XStack>
            </YStack>
            <YStack>
                <BodyMediumRegular>Update ID:</BodyMediumRegular>
                <XStack
                    gap={4}
                    alignItems="center"
                    cursor="pointer"
                    onPress={() => {
                        Clipboard.setString(updateId);
                        toast.show('Copied!', { duration: 1000 });
                    }}
                >
                    <BodyMediumRegular>{updateId}</BodyMediumRegular>
                    <Icon icon={CopyIcon} size={12} color="$black" />
                </XStack>
            </YStack>
        </YStack>
    );
};
